import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const MIN_FONT_SIZE = 85;
const MAX_FONT_SIZE = 130;
const FONT_SIZE_STEP = 5;
const DEFAULT_FONT_SIZE = 100;
const DEFAULT_LOCALE = 'en-US';
const PERSIST_KEY = 'mdvip-user-preferences';

interface UserPreferencesState {
  fontSize: number;
  timezone: string;
  locale: string;
}

interface UserPreferencesActions {
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  setTimezone: (tz: string) => void;
  setLocale: (locale: string) => void;
}

type UserPreferencesStore = UserPreferencesState & UserPreferencesActions;

function getDefaultTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getDefaultLocale(): string {
  return navigator.language || DEFAULT_LOCALE;
}

export const useUserPreferencesStore = create<UserPreferencesStore>()(
  devtools(
    persist(
      (set) => ({
        fontSize: DEFAULT_FONT_SIZE,
        timezone: getDefaultTimezone(),
        locale: getDefaultLocale(),

        increaseFontSize: () =>
          set(
            (s) => ({ fontSize: Math.min(s.fontSize + FONT_SIZE_STEP, MAX_FONT_SIZE) }),
            false,
            'userPreferences/increaseFontSize',
          ),

        decreaseFontSize: () =>
          set(
            (s) => ({ fontSize: Math.max(s.fontSize - FONT_SIZE_STEP, MIN_FONT_SIZE) }),
            false,
            'userPreferences/decreaseFontSize',
          ),

        resetFontSize: () => set({ fontSize: DEFAULT_FONT_SIZE }, false, 'userPreferences/resetFontSize'),

        setTimezone: (tz: string) => set({ timezone: tz }, false, 'userPreferences/setTimezone'),

        setLocale: (locale: string) => set({ locale }, false, 'userPreferences/setLocale'),
      }),
      {
        name: PERSIST_KEY,
        version: 1,
      },
    ),
    { name: 'userPreferencesStore' },
  ),
);

// Wire font size side effect outside React — only runs when fontSize changes
useUserPreferencesStore.subscribe((state, prevState) => {
  if (state.fontSize !== prevState.fontSize) {
    document.documentElement.style.fontSize = `${state.fontSize}%`;
  }
});

// Apply immediately on module load so the DOM reflects persisted state before first render
document.documentElement.style.fontSize = `${useUserPreferencesStore.getState().fontSize}%`;

// Wire lang attribute side effect outside React — WCAG 3.1.1 requires document.lang to match active locale
useUserPreferencesStore.subscribe((state, prevState) => {
  if (state.locale !== prevState.locale) {
    document.documentElement.lang = state.locale;
  }
});

// Apply immediately on module load
document.documentElement.lang = useUserPreferencesStore.getState().locale;

// Public hook — components use this instead of reading the store directly
export function useLocale(): string {
  return useUserPreferencesStore((s) => s.locale);
}
